import type { AnimationEvent, ComponentProps, CSSProperties } from "react";

import { cn } from "@/lib/utils";

/**
 * The mark cut into rows, on its 3x3 unit grid. The right-hand column is split
 * at the row lines, which is invisible at rest and lets each row turn on its
 * own. Same cells as `BrandMark`.
 */
const ROWS = [
  { x: 1, y: 0, width: 2 },
  { x: 0, y: 1, width: 1 },
  { x: 2, y: 1, width: 1 },
  { x: 1, y: 2, width: 2 }
];

/** Front, then each face the turn brings round (colours in motion.css). */
const FACES = [0, 1, 2, 3];

type Props = Omit<
  ComponentProps<"span">,
  "children" | "style" | "onAnimationEnd"
> & {
  /** Square size in px. A multiple of 3 keeps the rows on whole pixels. */
  size?: number;
  /**
   * `true` runs one turn; `"loop"` keeps turning, with a short hold at rest
   * between turns.
   */
  turning?: boolean | "loop";
  /** Fires when a single turn has finished on every row. */
  onTurnEnd?: () => void;
};

/**
 * The CaseBlink mark whose rows turn like blocks, as on the marketing site's
 * header logo. Colour the front face with a text class; brand indigo by
 * default. Decorative unless given an `aria-label`. Under
 * `prefers-reduced-motion` a looping mark pulses and a single turn is skipped.
 */
export function TurningMark({
  size = 15,
  turning = false,
  onTurnEnd,
  className,
  ...rest
}: Props) {
  const unit = size / 3;
  const handleAnimationEnd = (event: AnimationEvent<HTMLSpanElement>) => {
    const { row } = (event.target as HTMLElement).dataset;
    if (event.animationName === "brand-mark-turn" && row === "2") onTurnEnd?.();
  };

  return (
    <span
      aria-hidden={rest["aria-label"] ? undefined : true}
      data-slot="turning-mark"
      {...rest}
      data-turning={turning === "loop" ? "loop" : turning ? "" : undefined}
      onAnimationEnd={handleAnimationEnd}
      className={cn(
        "brand-turn relative inline-block shrink-0 text-accent-seed",
        className
      )}
      style={{ width: size, height: size }}
    >
      {ROWS.map((row) => (
        <span
          key={`${row.x}-${row.y}`}
          data-row={row.y}
          className="brand-turn-row absolute"
          style={
            {
              left: row.x * unit,
              top: row.y * unit,
              width: row.width * unit,
              height: unit,
              "--row": row.y
            } as CSSProperties
          }
        >
          {FACES.map((face) => (
            <span
              key={face}
              className="brand-turn-face"
              style={{ "--face": face } as CSSProperties}
            />
          ))}
        </span>
      ))}
    </span>
  );
}
